import React, { useEffect, useState } from 'react'

import i18n from 'i18n'
import { fetchMaintenanceHtml, injectMaintenanceLang } from 'services/maintenance'

import styles from './style.module.scss'

interface MaintenanceFrameProps {
  url: string
  title: string
  className?: string
  /** 표시 언어(ko|ja|en). confirmBase content(portal)에서도 쓸 수 있게 Redux 대신 prop 으로 받는다. */
  lang?: string
}

/**
 * 신뢰 도메인의 공지 HTML 을 iframe 으로 표시.
 * - iframe `src` 직접 로드는 cross-origin 응답의 CORP 부재 + 부모 COEP 정책으로 차단되므로,
 *   fetch(CORS 허용)로 본문을 받아 `srcDoc`(about:srcdoc, same-origin)으로 렌더한다.
 * - 다국어: 앱 lang 을 navigator.language override 로 주입(injectMaintenanceLang) → HTML 내
 *   다국어 JS 가 치환. 이를 위해 sandbox 에 allow-scripts 부여(allow-same-origin 은 미부여 →
 *   opaque origin 으로 부모와 격리). 콘텐츠는 도메인 화이트리스트 통과분이라 안전.
 * - sandbox: 문의 링크(target=_blank) 동작 위해 popup 허용.
 * - fetch 실패 시 한글 정적 안내로 대체(특히 blackout 빈 화면 방지).
 */
function MaintenanceFrame({
  url,
  title,
  className,
  lang,
}: MaintenanceFrameProps): React.ReactElement | null {
  const [html, setHtml] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setHtml(null)
    setFailed(false)
    fetchMaintenanceHtml(url)
      .then((result) => {
        if (cancelled) return
        if (!result) {
          setFailed(true)
          return
        }
        setHtml(injectMaintenanceLang(result, lang))
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [url, lang])

  if (failed) {
    return (
      <div className={styles.fallback}>
        <span>{i18n.t('maintenance.loadingFailed')}</span>
        <span>{i18n.t('maintenance.retryLater')}</span>
      </div>
    )
  }

  if (html === null) return null

  return (
    <iframe
      title={title}
      srcDoc={html}
      className={className ?? styles.frame}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
    />
  )
}

export default MaintenanceFrame
